import type { CallToolResult } from '@modelcontextprotocol/sdk/types.js';

import type { MarketDataEntry } from '../schemas';
import {
    type MarketAnalysis,
    MomentumIndicators,
    MovingAverages,
    OptionsAnalysis,
    PerformanceAnalysis,
    StatisticalModels,
    SupportResistanceIndicators,
    type TechnicalAnalysisOutput,
    type TechnicalIndicators,
    TradingSignalsGenerator,
    TrendIndicators,
    VolatilityIndicators,
    VolumeIndicators,
} from './indicators';

// Utility function to sum an array of numbers
function sum(numbers: number[]): number {
    return numbers.reduce((acc, val) => acc + val, 0);
}

// Technical Analysis Functions
class TechnicalAnalyzer {
    private prices: number[];
    private volumes: number[];
    private highs: number[];
    private lows: number[];

    constructor(data: MarketDataEntry[]) {
        // Use trade price as primary price, fallback to mid price if trade is 0
        this.prices = data.map((d) => (d.trade > 0 ? d.trade : d.midPrice));
        this.volumes = data.map((d) => d.volume);
        // Use session high/low prices, fallback to calculated values
        this.highs = data.map((d) => (d.tradingSessionHighPrice > 0 ? d.tradingSessionHighPrice : d.trade));
        this.lows = data.map((d) => (d.tradingSessionLowPrice > 0 ? d.tradingSessionLowPrice : d.trade));
    }

    // Calculate price changes for volatility
    private calculatePriceChanges(): number[] {
        const changes: number[] = [];
        for (let i = 1; i < this.prices.length; i++) {
            changes.push((this.prices[i] - this.prices[i - 1]) / this.prices[i - 1]);
        }
        return changes;
    }

    // Generate comprehensive market analysis
    public analyze(): MarketAnalysis {
        const currentPrice = this.prices[this.prices.length - 1];
        const startPrice = this.prices[0];
        const sessionHigh = Math.max(...this.highs);
        const sessionLow = Math.min(...this.lows);
        const totalVolume = sum(this.volumes);
        const avgVolume = totalVolume / this.volumes.length;

        const priceChanges = this.calculatePriceChanges();
        const volatility =
            priceChanges.length > 0
                ? Math.sqrt(
                      priceChanges.reduce((sum: number, change: number) => sum + change ** 2, 0) / priceChanges.length,
                  ) *
                  Math.sqrt(252) *
                  100
                : 0;

        const sessionReturn = ((currentPrice - startPrice) / startPrice) * 100;
        const pricePosition = ((currentPrice - sessionLow) / (sessionHigh - sessionLow)) * 100;
        const trueVWAP =
            this.prices.reduce((sum: number, price: number, i: number) => sum + price * this.volumes[i], 0) /
            totalVolume;

        const momentum5 =
            this.prices.length > 5
                ? ((currentPrice - this.prices[Math.max(0, this.prices.length - 6)]) /
                      this.prices[Math.max(0, this.prices.length - 6)]) *
                  100
                : 0;
        const momentum10 =
            this.prices.length > 10
                ? ((currentPrice - this.prices[Math.max(0, this.prices.length - 11)]) /
                      this.prices[Math.max(0, this.prices.length - 11)]) *
                  100
                : 0;

        const maxDrawdown = PerformanceAnalysis.calculateMaxDrawdown(this.prices);

        const atrValues = VolatilityIndicators.calculateATR(this.prices, this.highs, this.lows);
        const atr = atrValues.length > 0 ? atrValues[atrValues.length - 1] : 0;
        const impliedVolatility = volatility;
        const realizedVolatility = volatility;
        const sharpeRatio = sessionReturn / volatility;
        const sortinoRatio = sessionReturn / realizedVolatility;
        const calmarRatio = sessionReturn / maxDrawdown;
        const maxConsecutiveLosses = PerformanceAnalysis.calculateMaxConsecutiveLosses(this.prices);
        const winRate = PerformanceAnalysis.calculateWinRate(this.prices);
        const profitFactor = PerformanceAnalysis.calculateProfitFactor(this.prices);

        return {
            currentPrice,
            startPrice,
            sessionHigh,
            sessionLow,
            totalVolume,
            avgVolume,
            volatility,
            sessionReturn,
            pricePosition,
            trueVWAP,
            momentum5,
            momentum10,
            maxDrawdown,
            atr,
            impliedVolatility,
            realizedVolatility,
            sharpeRatio,
            sortinoRatio,
            calmarRatio,
            maxConsecutiveLosses,
            winRate,
            profitFactor,
        };
    }

    // Generate technical indicators
    public getTechnicalIndicators(): TechnicalIndicators {
        return {
            sma5: MovingAverages.calculateSMA(this.prices, 5),
            sma10: MovingAverages.calculateSMA(this.prices, 10),
            sma20: MovingAverages.calculateSMA(this.prices, 20),
            sma50: MovingAverages.calculateSMA(this.prices, 50),
            sma200: MovingAverages.calculateSMA(this.prices, 200),
            ema8: MovingAverages.calculateEMA(this.prices, 8),
            ema12: MovingAverages.calculateEMA(this.prices, 12),
            ema21: MovingAverages.calculateEMA(this.prices, 21),
            ema26: MovingAverages.calculateEMA(this.prices, 26),
            wma20: MovingAverages.calculateWMA(this.prices, 20),
            vwma20: MovingAverages.calculateVWMA(this.prices, this.volumes, 20),
            macd: TrendIndicators.calculateMACD(this.prices),
            adx: TrendIndicators.calculateADX(this.prices, this.highs, this.lows),
            dmi: TrendIndicators.calculateDMI(this.prices, this.highs, this.lows),
            ichimoku: TrendIndicators.calculateIchimoku(this.prices, this.highs, this.lows),
            parabolicSAR: TrendIndicators.calculateParabolicSAR(this.prices, this.highs, this.lows),
            rsi: MomentumIndicators.calculateRSI(this.prices, 14),
            stochastic: MomentumIndicators.calculateStochastic(this.prices, this.highs, this.lows),
            cci: MomentumIndicators.calculateCCI(this.prices, this.highs, this.lows),
            roc: MomentumIndicators.calculateROC(this.prices),
            williamsR: MomentumIndicators.calculateWilliamsR(this.prices),
            momentum: MomentumIndicators.calculateMomentum(this.prices),
            bollinger: VolatilityIndicators.calculateBollingerBands(this.prices, 20, 2),
            atr: VolatilityIndicators.calculateATR(this.prices, this.highs, this.lows),
            keltner: VolatilityIndicators.calculateKeltnerChannels(this.prices, this.highs, this.lows),
            donchian: VolatilityIndicators.calculateDonchianChannels(this.prices),
            chaikinVolatility: VolatilityIndicators.calculateChaikinVolatility(this.prices, this.highs, this.lows),
            obv: VolumeIndicators.calculateOBV(this.prices, this.volumes),
            cmf: VolumeIndicators.calculateCMF(this.prices, this.highs, this.lows, this.volumes),
            adl: VolumeIndicators.calculateADL(this.prices, this.highs, this.lows, this.volumes),
            volumeROC: VolumeIndicators.calculateVolumeROC(this.volumes),
            mfi: VolumeIndicators.calculateMFI(this.prices, this.highs, this.lows, this.volumes),
            vwap: VolumeIndicators.calculateVWAP(this.prices, this.volumes),
            pivotPoints: SupportResistanceIndicators.calculatePivotPoints(this.prices, this.highs, this.lows),
            fibonacci: SupportResistanceIndicators.calculateFibonacciLevels(this.prices),
            gannLevels: SupportResistanceIndicators.calculateGannLevels(this.prices),
            elliottWave: SupportResistanceIndicators.calculateElliottWave(this.prices),
            harmonicPatterns: SupportResistanceIndicators.calculateHarmonicPatterns(this.prices),
        };
    }

    // Generate comprehensive JSON analysis
    public generateJSONAnalysis(symbol: string): TechnicalAnalysisOutput {
        const analysis = this.analyze();
        const indicators = this.getTechnicalIndicators();
        const priceChanges = this.calculatePriceChanges();

        const currentSMA5 = indicators.sma5.length > 0 ? indicators.sma5[indicators.sma5.length - 1] : null;
        const currentSMA10 = indicators.sma10.length > 0 ? indicators.sma10[indicators.sma10.length - 1] : null;
        const currentSMA20 = indicators.sma20.length > 0 ? indicators.sma20[indicators.sma20.length - 1] : null;
        const currentSMA50 = indicators.sma50.length > 0 ? indicators.sma50[indicators.sma50.length - 1] : null;
        const currentSMA200 = indicators.sma200.length > 0 ? indicators.sma200[indicators.sma200.length - 1] : null;
        const currentEMA8 = indicators.ema8[indicators.ema8.length - 1];
        const currentEMA12 = indicators.ema12[indicators.ema12.length - 1];
        const currentEMA21 = indicators.ema21[indicators.ema21.length - 1];
        const currentEMA26 = indicators.ema26[indicators.ema26.length - 1];
        const currentWMA20 = indicators.wma20.length > 0 ? indicators.wma20[indicators.wma20.length - 1] : null;
        const currentVWMA20 = indicators.vwma20.length > 0 ? indicators.vwma20[indicators.vwma20.length - 1] : null;
        const currentMACD = indicators.macd.length > 0 ? indicators.macd[indicators.macd.length - 1] : null;
        const currentADX = indicators.adx.length > 0 ? indicators.adx[indicators.adx.length - 1] : null;
        const currentDMI = indicators.dmi.length > 0 ? indicators.dmi[indicators.dmi.length - 1] : null;
        const currentIchimoku =
            indicators.ichimoku.length > 0 ? indicators.ichimoku[indicators.ichimoku.length - 1] : null;
        const currentParabolicSAR =
            indicators.parabolicSAR.length > 0 ? indicators.parabolicSAR[indicators.parabolicSAR.length - 1] : null;
        const currentRSI = indicators.rsi.length > 0 ? indicators.rsi[indicators.rsi.length - 1] : null;
        const currentStochastic =
            indicators.stochastic.length > 0 ? indicators.stochastic[indicators.stochastic.length - 1] : null;
        const currentCCI = indicators.cci.length > 0 ? indicators.cci[indicators.cci.length - 1] : null;
        const currentROC = indicators.roc.length > 0 ? indicators.roc[indicators.roc.length - 1] : null;
        const currentWilliamsR =
            indicators.williamsR.length > 0 ? indicators.williamsR[indicators.williamsR.length - 1] : null;
        const currentMomentum =
            indicators.momentum.length > 0 ? indicators.momentum[indicators.momentum.length - 1] : null;
        const currentBB =
            indicators.bollinger.length > 0 ? indicators.bollinger[indicators.bollinger.length - 1] : null;
        const currentAtr = indicators.atr.length > 0 ? indicators.atr[indicators.atr.length - 1] : null;
        const currentKeltner = indicators.keltner.length > 0 ? indicators.keltner[indicators.keltner.length - 1] : null;
        const currentDonchian =
            indicators.donchian.length > 0 ? indicators.donchian[indicators.donchian.length - 1] : null;
        const currentChaikinVolatility =
            indicators.chaikinVolatility.length > 0
                ? indicators.chaikinVolatility[indicators.chaikinVolatility.length - 1]
                : null;
        const currentObv = indicators.obv.length > 0 ? indicators.obv[indicators.obv.length - 1] : null;
        const currentCmf = indicators.cmf.length > 0 ? indicators.cmf[indicators.cmf.length - 1] : null;
        const currentAdl = indicators.adl.length > 0 ? indicators.adl[indicators.adl.length - 1] : null;
        const currentVolumeROC =
            indicators.volumeROC.length > 0 ? indicators.volumeROC[indicators.volumeROC.length - 1] : null;
        const currentMfi = indicators.mfi.length > 0 ? indicators.mfi[indicators.mfi.length - 1] : null;
        const currentVwap = indicators.vwap.length > 0 ? indicators.vwap[indicators.vwap.length - 1] : null;
        const currentPivotPoints =
            indicators.pivotPoints.length > 0 ? indicators.pivotPoints[indicators.pivotPoints.length - 1] : null;
        const currentFibonacci =
            indicators.fibonacci.length > 0 ? indicators.fibonacci[indicators.fibonacci.length - 1] : null;
        const currentGannLevels = indicators.gannLevels.length > 0 ? indicators.gannLevels : [];
        const currentElliottWave =
            indicators.elliottWave.length > 0 ? indicators.elliottWave[indicators.elliottWave.length - 1] : null;
        const currentHarmonicPatterns = indicators.harmonicPatterns.length > 0 ? indicators.harmonicPatterns : [];

        const currentVolume = this.volumes[this.volumes.length - 1];
        const volumeRatio = currentVolume / analysis.avgVolume;
        const currentDrawdown = ((analysis.sessionHigh - analysis.currentPrice) / analysis.sessionHigh) * 100;
        const rangeWidth = ((analysis.sessionHigh - analysis.sessionLow) / analysis.sessionLow) * 100;
        const priceVsVWAP = ((analysis.currentPrice - analysis.trueVWAP) / analysis.trueVWAP) * 100;

        // Generate trading signals
        const signalData = TradingSignalsGenerator.generateSignals(
            analysis.currentPrice,
            analysis.trueVWAP,
            analysis.momentum5,
            analysis.momentum10,
            analysis.sessionReturn,
            currentVolume,
            analysis.avgVolume,
            analysis.pricePosition,
            analysis.volatility,
        );

        const tradingSignals = TradingSignalsGenerator.calculateOverallSignal(
            signalData.bullishSignals,
            signalData.bearishSignals,
            signalData.signals,
            analysis.volatility,
        );

        const targetEntry = Math.max(analysis.sessionLow * 1.005, analysis.trueVWAP * 0.998);
        const stopLoss = analysis.sessionLow * 0.995;
        const profitTarget = analysis.sessionHigh * 0.995;
        const riskRewardRatio = (profitTarget - analysis.currentPrice) / (analysis.currentPrice - stopLoss);

        const positionSize = PerformanceAnalysis.calculatePositionSize(targetEntry, stopLoss);
        const maxRisk = positionSize * (targetEntry - stopLoss);

        // Enhanced statistical models
        const varModel = StatisticalModels.calculateVAR(this.prices, this.volumes);
        const gaussianProcess = StatisticalModels.calculateGaussianProcess(this.prices);
        const pairsTrading = StatisticalModels.calculatePairsTrading(
            this.prices,
            this.prices.map((p) => p * 1.01),
        ); // Example with correlated asset
        const fourierTransform = StatisticalModels.calculateFourierTransform(this.prices);
        const emd = StatisticalModels.calculateEMD(this.prices);

        // Enhanced options analysis
        const blackScholes = OptionsAnalysis.calculateBlackScholes(
            analysis.currentPrice,
            analysis.startPrice,
            analysis.avgVolume,
        );
        const binomialTree = OptionsAnalysis.calculateBinomialTree(analysis.currentPrice, analysis.startPrice);
        const trinomialTree = OptionsAnalysis.calculateTrinomialTree(analysis.currentPrice, analysis.startPrice);
        const monteCarlo = OptionsAnalysis.calculateMonteCarlo(analysis.currentPrice, analysis.startPrice);
        const hestonModel = OptionsAnalysis.calculateHestonModel(analysis.currentPrice, analysis.startPrice);
        const sabrModel = OptionsAnalysis.calculateSABRModel(analysis.currentPrice, analysis.startPrice);
        const varianceGamma = OptionsAnalysis.calculateVarianceGamma(analysis.currentPrice, analysis.startPrice);

        return {
            symbol,
            timestamp: new Date().toISOString(),
            marketStructure: {
                currentPrice: analysis.currentPrice,
                startPrice: analysis.startPrice,
                sessionHigh: analysis.sessionHigh,
                sessionLow: analysis.sessionLow,
                rangeWidth,
                totalVolume: analysis.totalVolume,
                sessionPerformance: analysis.sessionReturn,
                positionInRange: analysis.pricePosition,
            },
            volatility: {
                impliedVolatility: analysis.impliedVolatility,
                realizedVolatility: analysis.realizedVolatility,
                atr: analysis.atr,
                maxDrawdown: analysis.maxDrawdown * 100,
                currentDrawdown,
            },
            technicalIndicators: {
                sma5: currentSMA5,
                sma10: currentSMA10,
                sma20: currentSMA20,
                sma50: currentSMA50,
                sma200: currentSMA200,
                ema8: currentEMA8,
                ema12: currentEMA12,
                ema21: currentEMA21,
                ema26: currentEMA26,
                wma20: currentWMA20,
                vwma20: currentVWMA20,
                macd: currentMACD,
                adx: currentADX,
                dmi: currentDMI,
                ichimoku: currentIchimoku,
                parabolicSAR: currentParabolicSAR,
                rsi: currentRSI,
                stochastic: currentStochastic,
                cci: currentCCI,
                roc: currentROC,
                williamsR: currentWilliamsR,
                momentum: currentMomentum,
                bollingerBands: currentBB
                    ? {
                          upper: currentBB.upper,
                          middle: currentBB.middle,
                          lower: currentBB.lower,
                          bandwidth: currentBB.bandwidth,
                          percentB: currentBB.percentB,
                      }
                    : null,
                atr: currentAtr,
                keltnerChannels: currentKeltner
                    ? {
                          upper: currentKeltner.upper,
                          middle: currentKeltner.middle,
                          lower: currentKeltner.lower,
                      }
                    : null,
                donchianChannels: currentDonchian
                    ? {
                          upper: currentDonchian.upper,
                          middle: currentDonchian.middle,
                          lower: currentDonchian.lower,
                      }
                    : null,
                chaikinVolatility: currentChaikinVolatility,
                obv: currentObv,
                cmf: currentCmf,
                adl: currentAdl,
                volumeROC: currentVolumeROC,
                mfi: currentMfi,
                vwap: currentVwap,
            },
            volumeAnalysis: {
                currentVolume,
                averageVolume: Math.round(analysis.avgVolume),
                volumeRatio,
                trueVWAP: analysis.trueVWAP,
                priceVsVWAP,
                obv: currentObv,
                cmf: currentCmf,
                mfi: currentMfi,
            },
            momentum: {
                momentum5: analysis.momentum5,
                momentum10: analysis.momentum10,
                sessionROC: analysis.sessionReturn,
                rsi: currentRSI,
                stochastic: currentStochastic,
                cci: currentCCI,
            },
            supportResistance: {
                pivotPoints: currentPivotPoints,
                fibonacci: currentFibonacci,
                gannLevels: currentGannLevels,
                elliottWave: currentElliottWave,
                harmonicPatterns: currentHarmonicPatterns,
            },
            tradingSignals,
            statisticalModels: {
                zScore: StatisticalModels.calculateZScore(analysis.currentPrice, analysis.startPrice),
                ornsteinUhlenbeck: StatisticalModels.calculateOrnsteinUhlenbeck(
                    analysis.currentPrice,
                    analysis.startPrice,
                    analysis.avgVolume,
                    priceChanges,
                ),
                kalmanFilter: StatisticalModels.calculateKalmanFilter(
                    analysis.currentPrice,
                    analysis.startPrice,
                    analysis.avgVolume,
                    priceChanges,
                ),
                pairsTrading,
                arima: StatisticalModels.calculateARIMA(analysis.currentPrice, priceChanges),
                garch: StatisticalModels.calculateGARCH(analysis.avgVolume, priceChanges),
                var: varModel,
                gaussianProcess,
                hilbertTransform: StatisticalModels.calculateHilbertTransform(analysis.currentPrice, priceChanges),
                waveletTransform: StatisticalModels.calculateWaveletTransform(analysis.currentPrice, priceChanges),
                fourierTransform,
                empiricalModeDecomposition: emd,
            },
            optionsAnalysis: (() => {
                if (!blackScholes) return null;

                return {
                    blackScholes,
                    binomialTree,
                    trinomialTree,
                    monteCarlo,
                    hestonModel,
                    sabrModel,
                    varianceGamma,
                    impliedVolatility: analysis.impliedVolatility,
                    delta: blackScholes.delta,
                    gamma: blackScholes.gamma,
                    theta: blackScholes.theta,
                    vega: blackScholes.vega,
                    rho: blackScholes.rho,
                    greeks: {
                        delta: blackScholes.delta,
                        gamma: blackScholes.gamma,
                        theta: blackScholes.theta,
                        vega: blackScholes.vega,
                        rho: blackScholes.rho,
                    },
                };
            })(),
            riskManagement: {
                targetEntry,
                stopLoss,
                profitTarget,
                riskRewardRatio,
                positionSize,
                maxRisk,
            },
            performance: {
                sharpeRatio: analysis.sharpeRatio,
                sortinoRatio: analysis.sortinoRatio,
                calmarRatio: analysis.calmarRatio,
                maxDrawdown: analysis.maxDrawdown * 100,
                winRate: analysis.winRate,
                profitFactor: analysis.profitFactor,
                totalReturn: analysis.sessionReturn,
                volatility: analysis.volatility,
            },
        };
    }
}

export const createTechnicalAnalysisHandler = (
    marketDataPrices: Map<string, MarketDataEntry[]>,
): ((args: { symbol: string }) => Promise<CallToolResult>) => {
    return async (args: { symbol: string }): Promise<CallToolResult> => {
        try {
            const symbol = args.symbol;
            const priceHistory = marketDataPrices.get(symbol) || [];

            if (priceHistory.length === 0) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `No price data available for ${symbol}. Please request market data first.`,
                            uri: 'technicalAnalysis',
                        },
                    ],
                };
            }

            // Validate data: must have valid trade and midPrice values
            const hasValidData = priceHistory.every(
                (entry) =>
                    typeof entry.trade === 'number' &&
                    !Number.isNaN(entry.trade) &&
                    typeof entry.midPrice === 'number' &&
                    !Number.isNaN(entry.midPrice),
            );
            if (!hasValidData) {
                throw new Error('Invalid market data');
            }

            // Convert MarketDataEntry to the format expected by TechnicalAnalyzer
            const analyzer = new TechnicalAnalyzer(priceHistory);
            const analysis = analyzer.generateJSONAnalysis(symbol);

            return {
                content: [
                    {
                        type: 'text',
                        text: `Technical Analysis for ${symbol}:\n\n${JSON.stringify(analysis, null, 2)}`,
                        uri: 'technicalAnalysis',
                    },
                ],
            };
        } catch (error) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error performing technical analysis: ${error instanceof Error ? error.message : 'Unknown error'}`,
                        uri: 'technicalAnalysis',
                    },
                ],
                isError: true,
            };
        }
    };
};
